// Broadcast composer hooks (marketing/fans) — the broadcast history, the log-only
// compose (nothing sends), per-broadcast delivery stats, and the email-style modal, over the generated
// client via the tenant-keyed wrappers. Keys: ['band', bandId, 'broadcasts'] (history, repainted by the
// `broadcast-changed` UI signal), ['band', bandId, 'broadcast', id] (stats), ['band', bandId, 'email-style'].
// fans:read / fans:manage gated. Compose is NEVER optimistic: explicit pending, server-confirm.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type BroadcastSummaryPaged = components["schemas"]["BroadcastSummaryPaged"];
export type BroadcastSummary = components["schemas"]["BroadcastSummary"];
export type BroadcastView = components["schemas"]["BroadcastView"];
export type BroadcastStats = components["schemas"]["BroadcastStats"];
export type SendBroadcastRequest = components["schemas"]["SendBroadcastRequest"];
export type EmailStyleView = components["schemas"]["EmailStyleView"];
export type EmailStyleRequest = components["schemas"]["EmailStyleRequest"];

/** GET the broadcast history (fans:read), newest first (first page). */
export function useBroadcasts() {
  const { api } = useApp();
  return useBandQuery<BroadcastSummaryPaged>(
    ["broadcasts"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/broadcasts", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

/** GET per-broadcast delivery/engagement stats (fans:read) — all zero at v1 (nothing sent). */
export function useBroadcastStats(broadcastId?: string) {
  const { api } = useApp();
  return useBandQuery<BroadcastStats>(
    ["broadcast", broadcastId ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/broadcasts/{broadcastId}/stats", {
        params: { path: { bandId, broadcastId: broadcastId! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!broadcastId },
  );
}

/** POST the log-only compose (fans:manage). NEVER optimistic — the history repaints on server-confirm (the
 *  broadcast-changed signal + the invalidate). Nothing sends.*/
export function useComposeBroadcast() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<BroadcastView, SendBroadcastRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/broadcasts", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "broadcasts"] }) },
  );
}

/** GET the band's email style (fans:read) — the designed default when unset (born-blank). */
export function useEmailStyle() {
  const { api } = useApp();
  return useBandQuery<EmailStyleView>(
    ["email-style"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/email-style", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  );
}

/** PUT the email-style upsert (fans:manage). Invalidates the style on success. */
export function useUpsertEmailStyle() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<EmailStyleView, EmailStyleRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/email-style", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "email-style"] }) },
  );
}
